import { Injectable, OnDestroy } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { io, Socket } from "socket.io-client";
import { environment } from "src/environments/environment";
import { ApplicationContextService } from "./application-context.service";

/**
 * Engage app realtime (wa-engage-v2 `SocketService` parity): `referral-updated`, `message-new`,
 * `referral-update`, `referral-new` — without streaming / StreamHandler dependencies.
 */
@Injectable({
  providedIn: "root",
})
export class EngageSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private connected = false;
  private readonly destroy$ = new Subject<void>();
  private readonly messageNew$ = new Subject<any>();
  private readonly referralUpdated$ = new Subject<any>();
  private readonly referralUpdate$ = new Subject<any>();
  private readonly referralNew$ = new Subject<any>();

  constructor(private readonly _appContextService: ApplicationContextService) {
    this._appContextService.context
      .pipe(
        filter((ctx) => !!ctx?.currentlyLoggedInUser?.id),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((ctx) => this.connect(ctx));
  }

  get messageNew(): Observable<any> {
    return this.messageNew$.asObservable();
  }

  get referralUpdated(): Observable<any> {
    return this.referralUpdated$.asObservable();
  }

  get referralUpdate(): Observable<any> {
    return this.referralUpdate$.asObservable();
  }

  get referralNew(): Observable<any> {
    return this.referralNew$.asObservable();
  }

  private connect(ctx: {
    currentlyLoggedInUser: { companyId?: string; id?: string };
  }): void {
    if (this.socket?.connected) {
      return;
    }

    const url = environment.engageSocketUrl;
    this.socket = io(url, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    this.socket.on("connect", () => {
      this.connected = true;
      const orgId = ctx.currentlyLoggedInUser.companyId;
      const userId = ctx.currentlyLoggedInUser.id;
      const registerData: Record<string, string> = {};
      if (orgId) {
        registerData.organizationId = orgId;
      }
      if (userId) {
        registerData.userId = userId;
      }
      if (Object.keys(registerData).length) {
        this.socket?.emit("register", registerData);
      }
    });

    this.socket.on("disconnect", () => {
      this.connected = false;
    });

    this.socket.on("message-new", (data: any) => this.messageNew$.next(data));
    this.socket.on("referral-updated", (data: any) =>
      this.referralUpdated$.next(data)
    );
    this.socket.on("referral-update", (data: any) =>
      this.referralUpdate$.next(data)
    );
    this.socket.on("referral-new", (data: any) => this.referralNew$.next(data));
    this.socket.on("referral", (data: any) => this.referralUpdate$.next(data));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.socket = null;
    }
    this.connected = false;
  }

  get isConnected(): boolean {
    return this.connected && this.socket?.connected === true;
  }
}
